import React from 'react';
import { Droppable } from '@hello-pangea/dnd';
import { cn } from '../../lib/utils';
import TaskCard from './TaskCard';
import type { TaskItemDto, TaskStatus } from '../../types/tasks';
import { statusLabel } from './StatusBadge';

interface KanbanColumnProps {
  status: TaskStatus;
  tasks: TaskItemDto[];
  onTaskClick: (task: TaskItemDto) => void;
}

const columnColors: Record<TaskStatus, string> = {
  Todo: 'border-t-slate-400',
  InProgress: 'border-t-blue-500',
  InReview: 'border-t-amber-500',
  Done: 'border-t-emerald-500',
};

const badgeColors: Record<TaskStatus, string> = {
  Todo: 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
  InProgress: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  InReview: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  Done: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
};

const KanbanColumn: React.FC<KanbanColumnProps> = ({ status, tasks, onTaskClick }) => {
  return (
    <div className={cn("flex flex-col bg-muted/30 rounded-lg border border-t-2 animate-fade-in-up", columnColors[status])}>
      {/* Column Header */}
      <div className="flex items-center justify-between px-3 py-2.5">
        <h3 className="text-sm font-semibold">{statusLabel[status]}</h3>
        <span className={cn("flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-medium", badgeColors[status])}>
          {tasks.length}
        </span>
      </div>

      {/* Droppable Zone */}
      <Droppable droppableId={status}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={cn(
              "flex-1 min-h-[200px] px-2 pb-2 transition-all duration-200",
              snapshot.isDraggingOver && "bg-primary/5 ring-1 ring-inset ring-primary/20"
            )}
          >
            {tasks.map((task, index) => (
              <TaskCard
                key={task.id}
                task={task}
                index={index}
                onClick={onTaskClick}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

export default KanbanColumn;
